// src/studio/components/ArtifactEditor.jsx
//
// Wraps one section's (or one GROUP item's) read-only rendering with the two
// ways a user can fix generated content: edit it directly, or describe a
// change and let the LLM suggest a revision -- reviewed and only written on
// an explicit Save either way. Ports the interaction shape of the older
// rds_ui pipeline's RefinementPanel.jsx + InlineEditor.jsx (Edit toggle vs.
// free-text instruction box, plain textarea + Save/Cancel) onto the Studio's
// document-run API, so it works uniformly for a flat PROSE/TABLE/DIAGRAM
// section (children = MarkdownView/TableSectionView/MermaidView) and for one
// GROUP item (children = GroupItemCard's field list) with no special-casing
// per section kind -- every one of them is just editing one artifact's
// content_markdown.
//
// "Ask AI" never saves on its own: preview-revision writes nothing (see
// preview_section_revision_handler.py's own docstring), so a bad suggestion
// never overwrites the user's last-saved content until they explicitly hit
// Save on it.
import { useState } from "react";
import PropTypes from "prop-types";
import { Loader2, Pencil, Sparkles, Check, X } from "lucide-react";
import { Alert } from "../../shared/ui";
import { studioDocumentsApi } from "../api/studioApi";

// idle -> editing (Edit clicked, or a suggestion came back from "asking")
// idle -> asking (Ask AI clicked, instruction box shown)
// any non-idle state -> idle (Cancel, or a successful Save)
const MODE = { IDLE: "idle", EDITING: "editing", ASKING: "asking" };

export default function ArtifactEditor({
  runId, sectionKey, groupKey = null, contentMarkdown = "", isJson = false,
  disabled = false, onSaved, children = null,
}) {
  const [mode, setMode] = useState(MODE.IDLE);
  const [draft, setDraft] = useState("");
  const [instruction, setInstruction] = useState("");
  const [asking, setAsking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const hasContent = Boolean(contentMarkdown && contentMarkdown.trim());

  function startEdit() {
    setError(null);
    setDraft(contentMarkdown || "");
    setMode(MODE.EDITING);
  }

  function startAsk() {
    setError(null);
    setInstruction("");
    setMode(MODE.ASKING);
  }

  function cancel() {
    setError(null);
    setMode(MODE.IDLE);
  }

  async function generateSuggestion() {
    setAsking(true);
    setError(null);
    try {
      const result = await studioDocumentsApi.previewSectionRevision(runId, sectionKey, {
        instruction, groupKey,
      });
      setDraft(result.content_markdown || "");
      setMode(MODE.EDITING);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to generate a suggestion.");
    } finally {
      setAsking(false);
    }
  }

  async function save() {
    if (isJson) {
      try {
        JSON.parse(draft);
      } catch {
        setError("This isn't valid JSON — fix the syntax before saving.");
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await studioDocumentsApi.updateSection(runId, sectionKey, {
        contentMarkdown: draft, groupKey,
      });
      onSaved(updated);
      setMode(MODE.IDLE);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  }

  if (mode === MODE.EDITING) {
    return (
      <div>
        <textarea
          className={[
            "w-full text-xs border border-brand-200 rounded-lg p-2.5 resize-y min-h-40",
            "focus:outline-none focus:ring-1 focus:ring-brand-400",
            isJson ? "font-mono" : "",
          ].join(" ")}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={saving}
          autoFocus
        />
        {error && <Alert variant="danger" className="mt-2">{error}</Alert>}
        <div className="flex gap-2 justify-end mt-2">
          <button
            type="button"
            onClick={cancel}
            disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
          >
            <X size={12} /> Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !draft.trim()}
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-40"
          >
            {saving
              ? <><Loader2 size={12} className="animate-spin" /> Saving…</>
              : <><Check size={12} /> Save</>}
          </button>
        </div>
      </div>
    );
  }

  if (mode === MODE.ASKING) {
    return (
      <div>
        <div className="flex gap-2 items-start">
          <textarea
            rows={2}
            className="flex-1 text-xs rounded-lg border border-gray-200 px-2.5 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-brand-400 disabled:opacity-50 disabled:bg-gray-50"
            placeholder="Describe what to change — e.g. 'Make this more specific to AWS Lambda timeouts'"
            value={instruction}
            disabled={asking}
            onChange={(e) => setInstruction(e.target.value)}
            autoFocus
          />
          <div className="flex flex-col gap-1 shrink-0">
            <button
              type="button"
              onClick={generateSuggestion}
              disabled={asking || instruction.trim().length < 5}
              className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-40"
            >
              {asking
                ? <><Loader2 size={12} className="animate-spin" /> Thinking…</>
                : <><Sparkles size={12} /> Generate</>}
            </button>
            <button
              type="button"
              onClick={cancel}
              disabled={asking}
              className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
            >
              <X size={12} /> Cancel
            </button>
          </div>
        </div>
        {error && <Alert variant="danger" className="mt-2">{error}</Alert>}
      </div>
    );
  }

  return (
    <div>
      {!disabled && hasContent && (
        <div className="flex gap-2 justify-end mb-2">
          <button
            type="button"
            onClick={startAsk}
            className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg border border-gray-200 text-slate-600 hover:bg-gray-50"
          >
            <Sparkles size={11} /> Ask AI
          </button>
          <button
            type="button"
            onClick={startEdit}
            className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg border border-gray-200 text-slate-600 hover:bg-gray-50"
          >
            <Pencil size={11} /> Edit
          </button>
        </div>
      )}
      {error && <Alert variant="danger" className="mb-2">{error}</Alert>}
      {children}
    </div>
  );
}

ArtifactEditor.propTypes = {
  runId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sectionKey: PropTypes.string.isRequired,
  groupKey: PropTypes.string,
  contentMarkdown: PropTypes.string,
  isJson: PropTypes.bool,
  disabled: PropTypes.bool,
  onSaved: PropTypes.func.isRequired,
  children: PropTypes.node,
};
